import type { TransformedSaveBowtieApiArg } from '../@types/enhanced-bowtie-api.types';
import { bowtieApi } from '../generated/bowtie-api';
import type {
  BackgroundJobConvertImageResponse,
  BowtieConvertImagePostApiArg,
  BowtieConvertImagePostApiResponse,
  BowtieFactorsJobPostApiArg,
  BowtieFactorsJobPostApiResponse,
  BowtieFactorsPostApiArg,
  BowtieFactorsPostApiResponse,
  BowtieHazardMueMaterialUnwantedEventsJobPostApiArg,
  BowtieHazardMueMaterialUnwantedEventsJobPostApiResponse,
  BowtieSuggestJobPostApiArg,
  BowtieSuggestJobPostApiResponse,
  ControlWithId,
  Detail,
  FactorId,
  SaveBowtieApiResponse,
} from '../generated/bowtie-api';

const injectedBowtieApi = bowtieApi.injectEndpoints({
  overrideExisting: true,
  endpoints: (build) => ({
    saveBowtie: build.mutation<SaveBowtieApiResponse, TransformedSaveBowtieApiArg>({
      query: (queryArg) => ({
        url: `/record`,
        method: 'POST',
        body: queryArg.bowtieDto,
        responseHandler: 'text',
      }),
    }),
    bowtieConvertImagePost: build.mutation<BowtieConvertImagePostApiResponse, BowtieConvertImagePostApiArg>({
      query: (queryArg) => ({
        url: `/ai/convert-image/start`,
        method: 'POST',
        body: queryArg.body,
        responseHandler: 'text',
      }),
    }),
    bowtieHazardMueMaterialUnwantedEventsJobPost: build.mutation<
      BowtieHazardMueMaterialUnwantedEventsJobPostApiResponse,
      BowtieHazardMueMaterialUnwantedEventsJobPostApiArg
    >({
      query: (queryArg) => ({
        url: `/ai/material-unwanted-events/start`,
        method: 'POST',
        body: queryArg.hazardMueRequest,
        responseHandler: 'text',
      }),
    }),
    bowtieFactorsJobPost: build.mutation<BowtieFactorsJobPostApiResponse, BowtieFactorsJobPostApiArg>({
      query: (queryArg) => ({
        url: `/ai/factors/start`,
        method: 'POST',
        body: queryArg.factorRequest,
        responseHandler: 'text',
      }),
    }),
    bowtieSuggestJobPost: build.mutation<BowtieSuggestJobPostApiResponse, BowtieSuggestJobPostApiArg>({
      query: (queryArg) => ({
        url: `/ai/suggest/start`,
        method: 'POST',
        body: queryArg.bowtieCorrectionRequest,
        responseHandler: 'text',
      }),
    }),
  }),
});

export const enhancedBowtieApi = injectedBowtieApi.enhanceEndpoints({
  addTagTypes: ['global-prompt', 'stored-diagram-filters', 'generated-diagram-filters'],
  endpoints: {
    bowtieConvertImageJobIdGet: {
      transformResponse(response: BackgroundJobConvertImageResponse) {
        return {
          ...response,
          result: {
            risk: response.result?.risk as string,
            hazard: response.result?.hazard as string,
            causes: response?.result?.causes.map((cause) => ({
              ...cause,
              uuid: cause.id,
              value: cause.name,
              controls: mapControls(cause.controls, cause.id),
              preventativeControls: mapControls(cause.controls, cause.id),
            })) as FactorId[],
            consequences: response?.result?.consequences.map((consequence) => ({
              ...consequence,
              uuid: consequence.id,
              value: consequence.name,
              controls: mapControls(consequence.controls, consequence.id),
              mitigatingControls: mapControls(consequence.controls, consequence.id),
            })) as FactorId[],

            scenario: response?.result?.risk,
          },
        };
      },
    },
    bowtieFactorsPost: {
      transformResponse: (response: BowtieFactorsPostApiResponse, _, args: BowtieFactorsPostApiArg) => {
        // align the shape of the data with the existing bowtie data

        const causes = response.causes.map((cause) => {
          const id = crypto.randomUUID();

          const causes = cause.controls.map((control: ControlWithId) => {
            return {
              ...control,
              value: control.name,
              parentId: id,
            };
          });

          return {
            ...cause,
            id: id,
            uuid: id,
            value: cause.name,
            controls: [...causes],
            preventativeControls: [...causes],
          };
        });

        const consequences = response.consequences.map((consequence) => {
          const id = crypto.randomUUID();

          const consequences = consequence.controls.map((control: ControlWithId) => {
            return {
              ...control,
              value: control.name,
              parentId: id,
            };
          });

          return {
            ...consequence,
            id: id,
            uuid: id,
            value: consequence.name,
            controls: [...consequences],
            mitigatingControls: [...consequences],
          };
        });

        return { causes, consequences, hazard: response.hazard ?? args.factorRequest.hazard ?? 'Hazard' } as Detail;
      },
    },
    getGlobalPrompt: {
      providesTags: ['global-prompt'],
    },
    updateGlobalPrompt: {
      invalidatesTags: ['global-prompt'],
    },
    getStoredDiagramFilters: {
      providesTags: ['stored-diagram-filters'],
    },
    updateStoredDiagramFilters: {
      invalidatesTags: ['stored-diagram-filters'],
    },
    deleteStoredDiagramFilters: {
      invalidatesTags: ['stored-diagram-filters'],
    },
    getGeneratedDiagramFilters: {
      providesTags: ['generated-diagram-filters'],
    },
    updateGeneratedDiagramFilters: {
      invalidatesTags: ['generated-diagram-filters'],
    },
    deleteGeneratedDiagramFilters: {
      invalidatesTags: ['generated-diagram-filters'],
    },
  },
});

const mapControls = (controls?: ControlWithId[], parentId?: string) => {
  return controls?.map((control) => ({
    ...control,
    value: control.name,
    uuid: control.id,
    parentId: parentId,
  }));
};

export const {
  useGetAllBowtiesQuery,
  useLazyGetAllBowtiesQuery,
  useSaveBowtieMutation,
  useGetBowtieByIdQuery,
  useLazyGetBowtieByIdQuery,
  useDeleteBowtieMutation,
  useUpdateBowtieMutation,
  useGetGlobalPromptQuery,
  useUpdateGlobalPromptMutation,
  useBowtieFactorsPostQuery,
  useLazyBowtieFactorsPostQuery,
  useBowtieConvertImageJobIdGetQuery,
  useGetStoredDiagramFiltersQuery,
  useLazyGetStoredDiagramFiltersQuery,
  useDeleteStoredDiagramFiltersMutation,
  useUpdateStoredDiagramFiltersMutation,
  useGetGeneratedDiagramFiltersQuery,
  useLazyGetGeneratedDiagramFiltersQuery,
  useDeleteGeneratedDiagramFiltersMutation,
  useUpdateGeneratedDiagramFiltersMutation,
  useBowtieConvertImagePostMutation,
  useBowtieHazardMueMaterialUnwantedEventsJobPostMutation,
  useBowtieFactorsJobPostMutation,
  useBowtieSuggestJobPostMutation,
} = enhancedBowtieApi;
